import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import "./Footer.css";
import { getTranslation } from "../utils/translation";
import { IMAGES } from "../utils/GetImages";
import { getAppUrls } from "../utils/config";

export default function Footer() {
  const { i18n } = useTranslation();
  const navigate = useNavigate();

  const iosAppDownload = IMAGES["AppDescriptionPage-ios_app_download.jpg"];
  const androidAppDownload =
    IMAGES["AppDescriptionPage-android_app_download.svg"];

  // Default links as fallback before remote config loads
  const [socialMediaMap, setSocialMediaMap] = useState([]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    (async () => {
      try {
        const res = await fetch(
          "https://d392t2kbzfe6yu.cloudfront.net/config/config.json",
          {
            cache: "no-store",
            headers: { Accept: "application/json" },
            mode: "cors",
            credentials: "omit",
            signal: controller.signal,
          }
        );
        console.log("[Footer] remote config status:", res.status);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        let data;
        try {
          data = await res.json();
        } catch (jsonErr) {
          const text = await res.text();
          const sanitized = text.replace(/,\s*([}\]])/g, "$1");
          data = JSON.parse(sanitized);
        }
        console.log("[Footer] remote config data:", data);
        const remote = data?.web?.socialMediaMap;
        console.log("[Footer] remote socialMediaMap:", remote);
        if (
          isMounted &&
          Array.isArray(remote) &&
          remote
            .map((u) => (typeof u === "string" ? u.trim() : ""))
            .filter((u) => u.length > 0).length > 0
        ) {
          setSocialMediaMap(
            remote
              .map((u) => (typeof u === "string" ? u.trim() : ""))
              .filter((u) => u.length > 0)
          );
          console.log("[Footer] socialMediaMap applied.");
        } else {
          console.warn(
            "[Footer] socialMediaMap invalid or empty; keeping current state."
          );
        }
      } catch (e) {
        console.warn("Failed to load remote socialMediaMap:", e);
      } finally {
        clearTimeout(timeoutId);
      }
    })();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, []);

  const text = getTranslation(i18n, "footer.socialMediaText");
  const split = (text || "").split(":");
  const prefix = split[0] || "";
  const linksPart = split[1] || "";
  const platforms = linksPart
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p.length > 0);

  return (
    <footer className="footer">
      <div className="footer-container">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-title">World Series Of Mahjong</h3>
            <p className="footer-description">
              {getTranslation(i18n, "footer.description")}
            </p>
            <div className="footer-description">
              <span>{prefix}:</span>
              <div className="social-media-icons">
                {socialMediaMap.map((url, idx) => {
                  const platform = platforms[idx];
                  const iconMap = {
                    YouTube: "/youtube.png",
                    RedNote: "/rednote.png",
                    Instagram: "/ig.png",
                  };
                  const iconSrc = iconMap[platform] || `/youtube.png`;

                  return url ? (
                    <a
                      key={`${platform}-${idx}`}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-media-link"
                    >
                      <img
                        src={iconSrc}
                        alt={platform}
                        className="social-media-icon"
                      />
                    </a>
                  ) : null;
                })}
              </div>
            </div>
            <div className="footer-download-app-buttons">
              <img
                src={iosAppDownload}
                className="footer-download-app-button"
                alt="Download for iOS"
                onClick={() => {
                  const appUrls = getAppUrls();
                  window.open(appUrls.iosUrl);
                }}
              />
              <img
                src={androidAppDownload}
                className="footer-download-app-button"
                alt="Download for Android"
                onClick={() => {
                  const appUrls = getAppUrls();
                  window.open(appUrls.androidUrl);
                }}
              />
            </div>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">
              {getTranslation(i18n, "footer.quickLinks")}
            </h4>
            <ul className="footer-links">
              <li>
                <span
                  onClick={() => navigate("/")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.home")}
                </span>
              </li>
              <li>
                <span
                  onClick={() => navigate("/latest-news")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.latestNews")}
                </span>
              </li>
              <li>
                <span
                  onClick={() => navigate("/wsom-app")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.ourApp")}
                </span>
              </li>
              <li>
                <span
                  onClick={() => navigate("/become-host")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.becomeAHost")}
                </span>
              </li>
              <li>
                <span
                  onClick={() => navigate("/shop")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.onlineShop")}
                </span>
              </li>
              <li>
                <span
                  onClick={() => navigate("/photo")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.photo")}
                </span>
              </li>
              <li>
                <span
                  onClick={() => navigate("/about-us")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.aboutUs")}
                </span>
              </li>
              <li>
                <span
                  onClick={() => navigate("/web-policy")}
                  style={{ cursor: "pointer" }}
                >
                  {getTranslation(i18n, "footer.links.webPolicy")}
                </span>
              </li>
            </ul>
          </div>
          <div className="footer-section">
            <h4 className="footer-heading">
              {getTranslation(i18n, "footer.contact")}
            </h4>
            <div className="footer-contact">
              <p>{getTranslation(i18n, "footer.email")}</p>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p className="footer-copyright">
            {getTranslation(i18n, "footer.copyright")}
          </p>
        </div>
      </div>
    </footer>
  );
}
